import classNames from 'classnames';
import produce from 'immer';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import type { RouteComponentProps } from 'react-router';
import { createStructuredSelector } from 'reselect';
import { isEmpty, isEqual } from 'underscore';

import type { UpdateProjectGroupReleaseRequest } from '../../../../typings/api';
import ProjectGroupsModule from '../../../redux/project_grouping';
import ProjectsModule from '../../../redux/projects';
import type { APIError, Project, ProjectGroup, ProjectGroupRelease, ProjectGroupReleaseProject } from '../../../types';
import { icons, IconText } from '../../generic/Icon';
import Loader from '../../generic/Loader';
import ConfirmReleaseDelete from './components/ConfirmReleaseDelete';
import ConfirmReleaseUnsavedChanges from './components/ConfirmReleaseUnsavedChanges';
import ProjectGroupListTable from './components/ProjectGroupListTable';
import ProjectGroupReleaseProjectsTable from './components/ProjectGroupReleaseProjectsTable';
import ProjectGroupReleaseProjectsTableHeader from './components/ProjectGroupReleaseProjectsTableHeader';
import ProjectGroupReleaseTitleInput from './components/ProjectGroupReleaseTitleInput';

type ProjectGroupReleasesPageProps = {
  projectGroup: ProjectGroup;
  projects: Project[];
  release: ProjectGroupRelease;
  releaseError?: APIError;
  projectStatus: {
    loading: boolean;
  };
  releaseIsLoading: boolean;
  selectedRelease: ProjectGroupRelease;
} & RouteComponentProps<Params, {}> & DispatchProps;

type DispatchProps = {
  fetchProjects: (filter: Record<string, any>) => void;
  fetchRelease: (projectGroupId: number, id: number) => void;
  updateRelease: (projectGroupId: number, id: number, data: any) => void;
  deleteRelease: (projectGroupId: number, id: number) => void;
  createRelease: (projectGroupId: number, data: any) => void;
  selectRelease: (projectGroupId: number, data: any) => void;
};

type ProjectGroupReleasesPageState = {
  showSelectProjects: boolean;
  projectsToRemove: ProjectGroupReleaseProject[];
  title: string;
  releaseProjects: ProjectGroupReleaseProject[];
  changed: boolean;
  showDeleteModal: boolean;
  showUnsavedChangesModal: boolean;
  releaseIdSelected: null | number;
  isNew: boolean;
};

type Params = {
  projectGroupId: number;
  releaseId: number;
};

export class ProjectGroupReleasesPage extends Component<ProjectGroupReleasesPageProps, ProjectGroupReleasesPageState> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };
  state = {
    title: this.props.release?.title,
    releaseProjects: this.props.release?.projects ?? [],
    showSelectProjects: false,
    projectsToRemove: [] as ProjectGroupReleaseProject[],
    changed: false,
    showDeleteModal: false,
    showUnsavedChangesModal: false,
    releaseIdSelected: null,
    isNew: false,
  };

  async componentDidMount() {
    await this.props.fetchProjects({ organizationId: window.user.organizationId, count: 500 });
    if (this.props.projectGroup) {
      if (this.props.params.releaseId) {
        return this.props.fetchRelease(this.props.params.projectGroupId, this.props.params.releaseId);
      }
      return this.props.fetchRelease(this.props.params.projectGroupId, this.props.selectedRelease.id);
    }
  }

  async componentDidUpdate(prevProps: ProjectGroupReleasesPageProps) {
    if (this.props.params.releaseId !== prevProps.params.releaseId) {
      await this.props.fetchRelease(this.props.params.projectGroupId, this.props.params.releaseId);
    }
    if (!isEqual(prevProps.release, this.props.release) && this.props.release) {
      this.setState(() => ({
        title: this.props.release.title,
        releaseProjects: this.props.release.projects ?? [],
        projectsToRemove: [],
        changed: false,
        isNew: false,
      }));
    }
  }

  onChangeTitle = (e) => {
    const title = e.target.value;
    const { isNew, releaseProjects } = this.state;
    this.setState({
      title,
      changed: (isNew && releaseProjects.length > 0) || title !== '',
    });
  };

  setShowSelectedProjects = () => {
    this.setState({
      showSelectProjects: true,
    });
  };

  onRemoveProject = (projectToRemove: ProjectGroupReleaseProject) => {
    this.setState(state => produce(state, (draft: ProjectGroupReleasesPageState) => {
      if (this.props.release.projects.find(project => project.projectId === projectToRemove.projectId)) {
        draft.projectsToRemove.push(projectToRemove);
      }
      draft.releaseProjects = draft.releaseProjects.filter(project => project.projectId !== projectToRemove.projectId);
      draft.changed = (state.isNew && draft.releaseProjects.length > 0) || draft.title !== '';
    }));
  };

  onDeleteRelease = async () => {
    this.toggleConfirmReleaseDelete();
  };

  onDeleteReleaseConfirm = async () => {
    this.toggleConfirmReleaseDelete();
    const { releases } = this.props.projectGroup;
    const releaseIndex = releases.findIndex(release => release.id === this.props.release.id);
    const release = releaseIndex === 0 ? releases[1] : releases[releaseIndex - 1];
    if (this.props.selectedRelease.id === this.props.release.id) {
      this.props.selectRelease(this.props.projectGroup.id, release.id);
    }
    await this.props.deleteRelease(this.props.params.projectGroupId, this.props.release.id);
    this.context.router.replace(`/projects/group/${this.props.projectGroup.id}/releases/${release.id}`);
  };

  onSaveRelease = async () => {
    const body: UpdateProjectGroupReleaseRequest = {
      title: this.state.title,
      projects: this.state.releaseProjects.map(project => ({
        projectId: project.projectId,
        branch: project.branch,
        revisionId: project.revision?.locator,
        projectGroupReleaseId: project.projectGroupReleaseId,
      })),
      projectsToDelete: this.state.projectsToRemove.map(project => project.projectId),
    };
    if (this.state.isNew) {
      await this.props.createRelease(this.props.params.projectGroupId, body);
    } else {
      await this.props.updateRelease(this.props.params.projectGroupId, this.props.release.id, body);
    }
  };

  toggleConfirmReleaseDelete = () => {
    this.setState(state => ({
      showDeleteModal: !state.showDeleteModal,
    }));
  };

  toggleUnsavedChanges = (id: number) => {
    this.setState(state => ({
      releaseIdSelected: id,
      showUnsavedChangesModal: !state.showUnsavedChangesModal,
    }));
  };

  onSelectRelease = (id: number) => () => {
    if (!this.state.isNew && id === this.props.release.id) {
      return;
    }
    if (this.state.changed || this.state.isNew) {
      this.toggleUnsavedChanges(id);
    } else {
      this.props.fetchRelease(this.props.params.projectGroupId, id);
    }
  };

  onCancelCreate = () => {
    this.setState({
      isNew: false,
      changed: false,
      title: this.props.release.title,
      releaseProjects: this.props.release.projects,
      projectsToRemove: [],
    });
  };

  onCreateNewRelease = () => {
    this.setState({
      isNew: true,
      title: '',
      changed: false,
      releaseProjects: this.props.release.projects.map((releaseProject) => ({
        projectId: releaseProject.projectId,
        branch: releaseProject.branch,
        project: releaseProject.project,
        revision: releaseProject.revision,
        revisionId: releaseProject.revisionId,
        latestRevision: releaseProject.latestRevision,
      })),
    });
  };

  onAcceptUnsavedChanges = () => {
    this.props.fetchRelease(this.props.params.projectGroupId, this.state.releaseIdSelected);
    this.toggleUnsavedChanges(null);
    if (this.state.isNew) {
      this.onCancelCreate();
    }
  };

  selectProject = (selectedProject: Project) => {
    this.setState(
      produce((draft: ProjectGroupReleasesPageState) => {
        let releaseProject = this.props.release.projects.find(project => project.projectId === selectedProject.locator);
        if (releaseProject) {
          draft.projectsToRemove = draft.projectsToRemove.filter(project => project.projectId !== selectedProject.locator);
        } else {
          releaseProject = {
            projectId: selectedProject.locator,
            branch: selectedProject.default_branch,
            project: selectedProject,
            revision: selectedProject.revisions[0] ?? null,
          };
        }
        draft.releaseProjects.push(releaseProject);
        if (this.state.isNew && this.state.title === '') {
          draft.changed = false;
        } else {
          draft.changed = true;
        }
      }),
    );
  };

  onUpdateProject = (updatedProject: ProjectGroupReleaseProject) => {
    this.setState(state => ({
      changed: true,
      releaseProjects: state.releaseProjects.map(project => {
        if (project.projectId === updatedProject.projectId) {
          return {
            ...project,
            ...updatedProject,
          };
        }
        return project;
      }),
    }));
  };

  get projects() {
    return this.props.projects.filter(
      (project) => project.locator && !this.state.releaseProjects.find(
        (releaseProject) => releaseProject.projectId === project.locator,
      ),
    );
  }

  render() {
    if (this.props.projectStatus.loading || this.props.releaseIsLoading || !this.props.release) {
      return <Loader />;
    }
    const hasEmptyRevisions = this.state.releaseProjects.some((project) => !project.revision);
    const releaseLabel = this.props.projectGroup.releases.length === 1 ? 'release' : 'releases';
    return (
      <div className="container edit-project-group-release">
        {this.props.releaseError && this.props.releaseError.message && (
          <div role="alert" className="alert alert-error alert-dismissable">
            Cannot save release group: {this.props.releaseError.message}
          </div>
        )}
        <ConfirmReleaseDelete
          release={this.props.release}
          show={this.state.showDeleteModal}
          onHide={this.toggleConfirmReleaseDelete}
          onConfirm={this.onDeleteReleaseConfirm}
        />
        <ConfirmReleaseUnsavedChanges
          show={this.state.showUnsavedChangesModal}
          onHide={this.toggleUnsavedChanges}
          onConfirm={this.onAcceptUnsavedChanges}
        />
        <div className="col-md-3">
          <div className="panel panel-default">
            <div className="panel-heading release-header">
              <div className="release-count">
                {this.props.projectGroup.releases.length} {releaseLabel}
              </div>
              <div>
                <Button
                  onClick={this.onCreateNewRelease}
                  disabled={this.state.isNew}
                >
                  <IconText
                    icon={icons.plusCircle}
                    text="Release"
                  />
                </Button>
              </div>
            </div>
            <div className="release-list">
              {this.state.isNew && (
                <div className="release release--active">
                  <div className="release__title">
                    {this.state.title}
                  </div>
                </div>
              )}
              {this.props.projectGroup.releases.map(release => (
                <div
                  key={release.id}
                  className={classNames('release', { 'release--active': !this.state.isNew && this.props.release.id === release.id })}
                  onClick={this.onSelectRelease(release.id)}
                >
                  <div className="release__title">
                    {release.title}
                  </div>
                  <div className="release__updated">
                    {moment(release.updatedAt).fromNow()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {(!isEmpty(this.props.release) || this.state.isNew) && (
          <div className="col-md-9">
            <div className="panel panel-default">
              <div className="panel-heading edit-release-header">
                <div>
                  <IconText
                    icon={icons.pencil}
                    text={this.state.isNew ? 'New Release' : 'Edit Release'}
                  />
                </div>
                <div className="edit-release-header__actions">
                  {this.state.isNew ? (
                    <Button
                      disabled={this.props.loading}
                      onClick={this.onCancelCreate}
                      bsStyle="danger"
                    >
                      Cancel
                    </Button>
                  ) : (
                    <Button
                      disabled={this.props.loading || this.props.projectGroup.releases.length === 1}
                      onClick={this.onDeleteRelease}
                      bsStyle="danger"
                    >
                      Delete release
                    </Button>
                  )}
                  <Button
                    disabled={
                      !this.state.changed
                      || this.props.loading
                      || this.state.releaseProjects.length === 0
                      || hasEmptyRevisions
                    }
                    onClick={this.onSaveRelease}
                    bsStyle="primary"
                  >
                    Save release
                  </Button>
                </div>
              </div>
              <div className="panel-body">
                <Form className="select-projects">
                  <Row>
                    <Col xs={6}>
                      <ProjectGroupReleaseTitleInput
                        title={this.state.title}
                        onChangeTitle={this.onChangeTitle}
                      />
                    </Col>
                  </Row>
                  <Row style={{ marginBottom: '10px' }}>
                    <Col xs={12}>
                      <ProjectGroupReleaseProjectsTableHeader
                        releaseProjects={this.state.releaseProjects}
                        showSelectProjects={this.state.showSelectProjects}
                        onShowSelectProjects={this.setShowSelectedProjects}
                        projectGroupId={this.props.projectGroup.id}
                        onUpdateProject={this.onUpdateProject}
                      />
                      <ProjectGroupReleaseProjectsTable
                        onProjectSelected={this.onRemoveProject}
                        releaseProjects={this.state.releaseProjects}
                        editMode={!this.state.isNew}
                        createMode={this.state.isNew}
                        onUpdateProject={this.onUpdateProject}
                      />
                    </Col>
                  </Row>
                  {
                    this.state.showSelectProjects && (
                      <div>
                        <Row>
                          <Col xs={12}>
                            <p className="text-label">All Projects</p>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={12}>
                            <ProjectGroupListTable
                              showHeader
                              onProjectSelected={this.selectProject}
                              projects={this.projects}
                            />
                          </Col>
                        </Row>
                      </div>
                    )
                  }
                </Form>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  projects: ProjectsModule.selectors.getProjectList,
  release: ProjectGroupsModule.selectors.getRelease,
  releaseError: ProjectGroupsModule.selectors.getReleaseError,
  projectStatus: ProjectsModule.selectors.getStatus,
  selectedRelease: ProjectGroupsModule.selectors.getSelectedRelease,
  releaseIsLoading: ProjectGroupsModule.selectors.getReleaseIsLoading,
});

const mapDispatchToProps = {
  fetchProjects: ProjectsModule.actions.fetchAll,
  fetchRelease: ProjectGroupsModule.actions.fetchRelease,
  createRelease: ProjectGroupsModule.actions.createRelease,
  updateRelease: ProjectGroupsModule.actions.updateRelease,
  deleteRelease: ProjectGroupsModule.actions.deleteRelease,
  selectRelease: ProjectGroupsModule.actions.selectRelease,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectGroupReleasesPage);
